import styled from 'styled-components';
import { IconChecking } from '@/app/common/icons/libraryIcons';
import { Text } from '@lmn/design-system';
import { checkboxHoverColor } from '@/app/components/search/styles/colors';
import { useIsDesktop } from '@/app/components/search/utils/useSsrMedia';
import {
  EvoFilterDivider,
  EvoFilterRadio,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';
import { getPriceIncrementLabel } from '@/app/components/hotel-detail/changeTransport/priceIncrement.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import {
  getSortFilter,
  getSortOptionName,
  sortEventOriginMap,
  sortOptionsMap,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportSorting';
import type { SortFilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import useTransportSorting from '@/app/components/hotel-detail/hooks/useTransportSorting';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { IconExternalLink } from '@/app/components/hotel-detail/common/icons';

const SortOptionRow = styled.div<{ $isDesktop?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  /* MPH-474: exact 48px row per Figma; the desktop menu item insets its hover box 16px into the
     24px content padding (8px text inset), mobile keeps the 12px inset from MPH-398 */
  min-height: 48px;
  margin: 0 ${({ $isDesktop }) => ($isDesktop ? '-8px' : '-12px')};
  padding: 0 ${({ $isDesktop }) => ($isDesktop ? '8px' : '12px')};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${checkboxHoverColor};
  }
`;

// MPH-398: price increment shown on the right of each sort option (Recommended has none).
const SortPrice = styled.span`
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
`;

/* MPH-474: Figma desktop sort rows (683:39118) mark the selected option with a check on the right,
   no radio and no price; mobile (683:39148) keeps the radio + price rows from MPH-398 */
const SelectedIcon = styled(IconChecking)`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  min-width: 20px;
  min-height: 20px;
`;

const RankingLinkRow = styled.div<{ $isDesktop?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  /* MPH-474: on desktop the divider above already provides the 16px Figma gap */
  padding: ${({ $isDesktop }) => ($isDesktop ? '0' : '8px 0')};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
`;

const RankingLink = styled.a`
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  }
`;

type EvoSortFilterProps = {
  // MPH-398: the Evo sort sheet is deferred — the radio selection is staged via onChange and applied
  // only on "Apply" (so the selection state is read from the staged sortOptions passed in).
  sortOptions?: SortFilter[];
  onChange: (filterChange: SortFilterChange) => void;
};

export function EvoSortFilter(props: EvoSortFilterProps) {
  const { sortOptions, onChange } = props;
  const { data: sortData } = useTransportSorting();
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { commonData, localization } = usePageWrapperData();
  const { showTotalPrice, toDisplayPrice } = usePriceDisplay();
  const priceUnitLabel = t(showTotalPrice ? 'vacation.oss.cards.total' : 'vacation.oss.cards.pp');
  const isDesktop = useIsDesktop(commonData.isDesktopDevice);
  const recommendedSortingInfoUrl = localization.rankingLink;

  const onSortChange = (sortOption: TransportSortOption) => {
    if (!sortOptions) return;
    // MPH-398: the "click_transport_sorting" curiosity event is sent when the sort is actually
    // applied (in EvoTransportFilters), not on each staged radio tap on mobile.
    onChange({
      newState: { sortOptions: getSortFilter(sortOption, sortOptions) },
      event: { type: 'SORT', origin: sortEventOriginMap[sortOption] },
    } as SortFilterChange);
  };

  const selectedSortFilterOption = sortOptions?.find((option) => option.selected)?.option;
  const selectedSortOption = selectedSortFilterOption ? sortOptionsMap[selectedSortFilterOption] : null;
  // MPH-1217: when the selected filters leave no flights, the response carries no sort options and there is no
  // price delta to show, but the sort pill stays; list the options of the filter state, without prices.
  const sortRows: { sortOption: TransportSortOption; priceIncrement?: number }[] =
    sortData.length > 0 ? sortData : (sortOptions ?? []).map(({ option }) => ({ sortOption: sortOptionsMap[option] }));

  return (
    <>
      <div role="radiogroup" aria-label={t('vacation.oss.components.searchresults.sort.sortby')}>
        {sortRows.map(({ sortOption, priceIncrement }) => {
          const optionLabel =
            sortOption === 'BERTOLI'
              ? t('vacation.oss.components.searchresults.sort.recommended')
              : getSortOptionName({ sortOption, t, isTrain });
          return (
            <SortOptionRow
              $isDesktop={isDesktop}
              role="radio"
              tabIndex={0}
              aria-checked={selectedSortOption === sortOption}
              key={sortOption}
              onClick={() => onSortChange(sortOption)}
              onKeyUp={keyUpHandler(() => onSortChange(sortOption))}
            >
              {isDesktop ? (
                <Text variant="Body" color="textPrimary">
                  {optionLabel}
                </Text>
              ) : (
                /* MPH-398: visual radio only; the SortOptionRow is the accessible radio (role="radio"),
                   so the DS control is hidden from assistive tech to avoid duplicate radio semantics. */
                <EvoFilterRadio
                  label={optionLabel}
                  value={sortOption}
                  checked={selectedSortOption === sortOption}
                  readOnly={true}
                  aria-hidden={true}
                  tabIndex={-1}
                  css={`
                    pointer-events: none;
                  `}
                />
              )}
              {/* MPH-398: mobile Figma shows the price delta (sign + amount + unit suffix) for every
                  option except the recommended baseline, e.g. "- £34 pp" / "+ £73 pp". */}
              {!isDesktop && sortOption !== 'BERTOLI' && priceIncrement != null && (
                <SortPrice>
                  {`${getPriceIncrementLabel({
                    priceIncrement: toDisplayPrice(priceIncrement),
                    localization,
                  })} ${priceUnitLabel}`}
                </SortPrice>
              )}
              {isDesktop && selectedSortOption === sortOption && <SelectedIcon aria-hidden={true} />}
            </SortOptionRow>
          );
        })}
      </div>
      {/* MPH-474: Figma desktop sort (683:39118) separates the ranking link with a divider; mobile doesn't */}
      {isDesktop && <EvoFilterDivider />}
      <RankingLinkRow $isDesktop={isDesktop}>
        <span
          css={`
            margin-right: 0.5ch;
          `}
        >
          {t('vacation.flightavailability.sort.sorttype.popular.explanation.label')}
        </span>
        <RankingLink
          href={recommendedSortingInfoUrl}
          target="_blank"
          rel="noreferrer"
          css={`
            margin-right: 0.5ch;
          `}
        >
          {/* MPH-398: Figma underlines "ranking criteria" (both words) */}
          {t('vacation.flightavailability.sort.sorttype.popular.explanation.shortlink')}
        </RankingLink>
        <IconExternalLink color="gray400" />
      </RankingLinkRow>
    </>
  );
}
